import { supabase } from "../../lib/supabase";
import { isValidWeightKg } from "./v2NutritionUtils";
import { normalizeV2Role, V2_ROLES } from "./v2Permissions";
import { loadV2Settings, readV2Json, removeV2Json, saveV2Settings, writeV2Json } from "./v2Storage";

export const V2_REGISTRATION_STAGES = Object.freeze({
  awaitingEmailVerification: "awaiting_email_verification",
  setPassword: "set_password",
  complete: "complete",
});

export const V2_PENDING_REGISTRATION_KEY = "ddp-v2-pending-registration";

export function isAnonymousUser(user) {
  return Boolean(user?.is_anonymous || user?.app_metadata?.provider === "anonymous");
}

export function isPermanentUser(user) {
  return Boolean(user?.id && !isAnonymousUser(user));
}

export function getRegistrationStage(user) {
  return user?.user_metadata?.registration_stage || "";
}

export function isAwaitingEmailVerification(user) {
  return getRegistrationStage(user) === V2_REGISTRATION_STAGES.awaitingEmailVerification;
}

export function hasEmailIdentity(user) {
  const identities = user?.identities || [];
  if (!identities.length) return Boolean(user?.email);

  return identities.some((identity) => {
    return Boolean(
      identity?.provider === "email" ||
        identity?.identity_data?.email ||
        identity?.email
    );
  });
}

export function hasVerifiedEmailIdentity(user, expectedEmail = "") {
  const normalizedExpected = String(expectedEmail || "").trim().toLowerCase();
  const normalizedUserEmail = String(user?.email || "").trim().toLowerCase();

  if (normalizedExpected && normalizedUserEmail !== normalizedExpected) return false;
  if (user?.email_confirmed_at || user?.confirmed_at) return true;

  return Boolean(
    (user?.identities || []).some((identity) => {
      const identityEmail = String(identity?.identity_data?.email || identity?.email || "").trim().toLowerCase();
      if (normalizedExpected && identityEmail && identityEmail !== normalizedExpected) return false;
      return Boolean(identity?.identity_data?.email_verified === true || identity?.confirmed_at);
    })
  );
}

export function needsPasswordSetup(user) {
  return (
    isPermanentUser(user) &&
    getRegistrationStage(user) === V2_REGISTRATION_STAGES.setPassword &&
    hasVerifiedEmailIdentity(user)
  );
}

export function resolveV2SessionState({ localSession, user, settings = {} } = {}) {
  const anonymous = isAnonymousUser(user);
  const pendingPasswordSetup = needsPasswordSetup(user);
  const registrationStage = getRegistrationStage(user);
  const awaitingEmailVerification = isAwaitingEmailVerification(user) || localSession?.mode === "awaiting_email_verification";
  const guest = Boolean(localSession?.mode === "guest" && anonymous);
  const locallyRegistered = Boolean(localSession?.mode === "registered" && localSession?.user?.id === user?.id);
  const registered = Boolean(
    isPermanentUser(user) &&
      !awaitingEmailVerification &&
      !pendingPasswordSetup &&
      (registrationStage === V2_REGISTRATION_STAGES.complete || locallyRegistered)
  );

  return {
    isAnonymous: anonymous,
    isGuest: guest,
    isRegistered: registered,
    needsPasswordSetup: pendingPasswordSetup,
    hasV2Session: guest || registered,
    hasRequiredWeight: isValidWeightKg(settings.profile?.weight_kg),
  };
}

export function getAuthProfile(user = {}) {
  return {
    full_name: user.user_metadata?.full_name || user.email?.split("@")[0] || "",
    weight_kg: user.user_metadata?.weight_kg || user.user_metadata?.weight || "",
  };
}

export function validateAnonymousRegistrationStart(data = {}, anonymousUserId = "") {
  const user = data?.user;
  if (!user?.id) {
    return {
      ok: false,
      error: "Registration could not be started.",
    };
  }

  if (anonymousUserId && user.id !== anonymousUserId) {
    return {
      ok: false,
      error: "Registration must keep the current anonymous user.",
    };
  }

  if (data?.session?.user && !isAnonymousUser(data.session.user)) {
    return {
      ok: false,
      error: "Registration cannot continue because email verification is not enforced.",
    };
  }

  const emailSent = Boolean(user.confirmation_sent_at || user.email_change_sent_at);
  if (!emailSent || !isAnonymousUser(user)) {
    return {
      ok: false,
      error: "Registration email was not sent. Check Supabase email confirmation and SMTP settings.",
    };
  }

  return { ok: true, error: "" };
}

export function validateVerifiedRegistrationCallback({ verifiedSession, pending } = {}) {
  const verifiedUser = verifiedSession?.user;
  if (!verifiedSession?.access_token || !verifiedSession?.refresh_token || !verifiedUser?.id) {
    return { ok: false, error: "Invalid or expired link." };
  }

  if (!isPermanentUser(verifiedUser) || !hasEmailIdentity(verifiedUser) || !hasVerifiedEmailIdentity(verifiedUser)) {
    return { ok: false, error: "Email verification is not complete." };
  }

  if (getRegistrationStage(verifiedUser) !== V2_REGISTRATION_STAGES.awaitingEmailVerification) {
    return { ok: false, error: "Invalid or expired link." };
  }

  if (
    pending?.anonymous_user_id &&
    pending.anonymous_user_id !== verifiedUser.id
  ) {
    return { ok: false, error: "This verification link does not match the pending registration." };
  }

  if (
    pending?.anonymous_user_id &&
    pending.anonymous_user_id === verifiedUser.id &&
    pending.email &&
    String(verifiedUser.email || "").trim().toLowerCase() !== String(pending.email || "").trim().toLowerCase()
  ) {
    return { ok: false, error: "This verification link does not match the pending registration." };
  }

  return { ok: true, user: verifiedUser, error: "" };
}

export async function verifyCompleteRegistrationCallback({ tokenHash, type, code } = {}, { auth = supabase.auth } = {}) {
  if (tokenHash) {
    if (type !== "email_change") {
      return { ok: false, session: null, error: "Invalid or expired link." };
    }

    const result = await auth.verifyOtp({
      token_hash: tokenHash,
      type: "email_change",
    });

    if (result.error || !result.data?.session?.access_token || !result.data?.session?.refresh_token) {
      return { ok: false, session: null, error: "Invalid or expired link." };
    }

    return { ok: true, session: result.data.session, error: "" };
  }

  if (code) {
    const result = await auth.exchangeCodeForSession(code);
    if (result.error || !result.data?.session?.access_token || !result.data?.session?.refresh_token) {
      return { ok: false, session: null, error: "Invalid or expired link." };
    }

    return { ok: true, session: result.data.session, error: "" };
  }

  return { ok: false, session: null, error: "Invalid or expired link." };
}

export function createPendingRegistrationRecord({ anonymousUser, email, full_name, weight_kg }) {
  return {
    anonymous_user_id: anonymousUser?.id || "",
    email: String(email || "").trim().toLowerCase(),
    full_name: String(full_name || "").trim() || "Patient",
    weight_kg,
    created_at: new Date().toISOString(),
  };
}

export function savePendingRegistration(record) {
  return writeV2Json(V2_PENDING_REGISTRATION_KEY, record);
}

export function loadPendingRegistration() {
  const pending = readV2Json(V2_PENDING_REGISTRATION_KEY, null);
  return pending?.anonymous_user_id && pending?.email ? pending : null;
}

export function clearPendingRegistration() {
  removeV2Json(V2_PENDING_REGISTRATION_KEY);
}

export function getSafeAuthMessage(error, fallback = "Something went wrong. Please try again.") {
  const message = String(error?.message || error || "").toLowerCase();

  if (message.includes("email not confirmed") || message.includes("not confirmed")) {
    return "Email not confirmed. Please check your inbox before signing in.";
  }
  if (message.includes("rate limit") || message.includes("too many") || message.includes("over_email_send_rate_limit")) {
    return "Rate limit reached. Please wait a few minutes and try again.";
  }
  if (message.includes("invalid login") || message.includes("invalid credentials")) {
    return "Invalid email or password.";
  }
  if (message.includes("already registered") || message.includes("already been registered") || message.includes("already exists")) {
    return "This email already belongs to an account. Please sign in instead.";
  }

  return fallback;
}

export async function loadOrBootstrapV2Profile(user, { client = supabase } = {}) {
  if (!isPermanentUser(user)) {
    return { ok: false, profile: null, error: "A permanent account is required." };
  }

  const existing = await client
    .from("v2_profiles")
    .select("user_id, full_name, weight_kg, created_at, updated_at")
    .eq("user_id", user.id)
    .maybeSingle();

  if (existing.error) {
    return { ok: false, profile: null, error: "Profile loading error." };
  }

  if (existing.data) {
    hydrateLocalCompatibilityProfile(existing.data);
    return { ok: true, profile: existing.data, error: "" };
  }

  const metadataProfile = getAuthProfile(user);
  const profile = {
    user_id: user.id,
    full_name: String(metadataProfile.full_name || "Patient").trim() || "Patient",
    weight_kg: isValidWeightKg(metadataProfile.weight_kg) ? Number(metadataProfile.weight_kg) : null,
  };
  const upserted = await client
    .from("v2_profiles")
    .upsert(profile, { onConflict: "user_id" })
    .select("user_id, full_name, weight_kg, created_at, updated_at")
    .single();

  if (upserted.error) {
    return { ok: false, profile: null, error: "Profile loading error." };
  }

  hydrateLocalCompatibilityProfile(upserted.data);
  return { ok: true, profile: upserted.data, error: "" };
}

export async function loadV2AccessRole(user, { client = supabase } = {}) {
  if (!isPermanentUser(user)) {
    return { ok: true, role: V2_ROLES.user, error: "" };
  }

  const result = await client
    .from("profiles")
    .select("role")
    .eq("id", user.id)
    .maybeSingle();

  if (result.error) {
    return { ok: false, role: V2_ROLES.user, error: "Role loading error." };
  }

  return { ok: true, role: normalizeV2Role(result.data?.role), error: "" };
}

export async function upsertV2Profile(profile, { client = supabase, user } = {}) {
  const authUser = user || (await client.auth.getUser()).data?.user;
  if (!isPermanentUser(authUser)) {
    return { ok: false, profile: null, error: "A permanent account is required." };
  }

  const row = {
    user_id: authUser.id,
    full_name: String(profile.full_name || "").trim() || "Patient",
    weight_kg: isValidWeightKg(profile.weight_kg) ? Number(profile.weight_kg) : null,
  };

  const result = await client
    .from("v2_profiles")
    .upsert(row, { onConflict: "user_id" })
    .select("user_id, full_name, weight_kg, created_at, updated_at")
    .single();

  if (result.error) {
    return { ok: false, profile: null, error: "Profile loading error." };
  }

  await client.auth.updateUser({
    data: {
      full_name: row.full_name,
      weight_kg: row.weight_kg,
      registration_stage: getRegistrationStage(authUser) || V2_REGISTRATION_STAGES.complete,
    },
  });
  hydrateLocalCompatibilityProfile(result.data);
  return { ok: true, profile: result.data, error: "" };
}

export function hydrateLocalCompatibilityProfile(profile = {}) {
  const weightKg = profile.weight_kg ?? "";
  const currentSettings = loadV2Settings();
  saveV2Settings({
    ...currentSettings,
    profile: {
      ...currentSettings.profile,
      full_name: profile.full_name || currentSettings.profile.full_name || "",
      weight: weightKg || "",
      weight_kg: weightKg || "",
    },
  });
}

export async function hasAnonymousAuthData(userId, { client = supabase } = {}) {
  if (!userId) return { ok: true, hasData: false, error: "" };

  const checks = await Promise.all([
    client.from("v2_medication_reminders").select("id").eq("user_id", userId).eq("active", true).limit(1),
    client.from("v2_push_subscriptions").select("id").eq("user_id", userId).eq("active", true).limit(1),
    client
      .from("v2_notification_settings")
      .select("user_id")
      .eq("user_id", userId)
      .or("whatsapp_reminders_enabled.eq.true,push_notifications_enabled.eq.true,daily_system_reminder_enabled.eq.true")
      .limit(1),
  ]);

  const failed = checks.find((result) => result.error);
  if (failed) return { ok: false, hasData: false, error: "Could not inspect this device's anonymous reminders." };

  return { ok: true, hasData: checks.some((result) => (result.data || []).length > 0), error: "" };
}

export async function transferAnonymousAuthData({ anonymousAccessToken, permanentAccessToken, mode, fetchImpl = fetch }) {
  const response = await fetchImpl("/.netlify/functions/transfer-anonymous-auth-data", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${permanentAccessToken}`,
      "X-Anonymous-Authorization": `Bearer ${anonymousAccessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ mode }),
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data?.ok) {
    return { ok: false, error: data?.error || "Anonymous reminder handoff failed." };
  }

  return data;
}
